import { useInfiniteQuery } from '@tanstack/react-query'
import { useEffect } from 'react'

import { FILTER_DEFAULT_TAKE } from '@apps-common/config/filter'
import { axios, queryClient } from '@apps-common/lib'
import { IUnitControlQueueBasicModel } from '@apps-common/types/portal/unit-control'

export const QUERY_KEY_MY_MONITORING_GET_CONTROL_HISTORY_BY_CONTROL = (controlId?: number, take?: number) =>
  ['getMyMonitoringControlHistoryByControl', controlId, take].filter(Boolean)

const getUnitControlHistoryByControl = async (
  controlId?: number,
  skip = 0,
  take = FILTER_DEFAULT_TAKE
): Promise<IUnitControlQueueBasicModel[]> =>
  axios
    .get(`/my-monitoring/control/GetControlHistoryByControl?controlId=${controlId}&skip=${skip}&take=${take}`)
    .then((res) => res.data?.data)

type Props = {
  controlId: number
  take: number
  enabled?: boolean
}

export const useMyMonitoringGetUnitControlHistoryByControl = ({ controlId, take, enabled }: Props) => {
  const { isLoading, isFetching, isRefetching, isError, data, refetch, fetchNextPage, hasNextPage } = useInfiniteQuery({
    ...{ cacheTime: 0 },
    queryKey: QUERY_KEY_MY_MONITORING_GET_CONTROL_HISTORY_BY_CONTROL(controlId, take),
    initialPageParam: 0,
    enabled,
    queryFn: ({ pageParam = 0 }) => getUnitControlHistoryByControl(controlId, pageParam, take),
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage?.length < take) {
        return null
      }

      return allPages?.reduce((acc, curr) => acc + (curr?.length || 0), 0)
    }
  })

  const refetchOverride = async () => {
    await queryClient.cancelQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_CONTROL_HISTORY_BY_CONTROL() })
    queryClient.removeQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_CONTROL_HISTORY_BY_CONTROL() })
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_CONTROL_HISTORY_BY_CONTROL() })
    refetch()
  }

  useEffect(
    () => () => {
      queryClient.removeQueries({ queryKey: QUERY_KEY_MY_MONITORING_GET_CONTROL_HISTORY_BY_CONTROL() })
    },
    []
  )

  return { isLoading, isFetching, isRefetching, isError, data, refetch: refetchOverride, fetchNextPage, hasNextPage }
}
